import {useEffect, useMemo, useRef} from "react";
import {useFormContext} from "react-hook-form";

// Build a stable key from an array of {currency, price}
const toKey = (arr?: {currency: string; price: number}[]) =>
  (arr ?? []).map((x) => `${x.currency}:${x.price}`).join("|");

export function useVariantCurrenciesEnforcer({
  variantIndex,
  optionIndex,
  productSelectedPriceType, // "paid" | "free"
  productCurrencies, // Array<{ callPriceCurrency: string }>
  replace, // RHF useFieldArray().replace
}: {
  variantIndex: number;
  optionIndex: number;
  productSelectedPriceType: "paid" | "free";
  productCurrencies: {callPriceCurrency: string}[];
  replace: (rows: {currency: string; price: number}[]) => void;
}) {
  const {getValues} = useFormContext();

  // RHF path to the currencies array for this variant option
  const path = `variants.${variantIndex}.options.${optionIndex}.currencies` as const;

  // Guards to prevent redundant replace calls
  const lastAppliedKeyRef = useRef<string | null>(null);

  // Memoized key for product currency list (so effect only runs when this changes)
  const productKey = useMemo(
    () =>
      productCurrencies
        .map((c) => c.callPriceCurrency)
        .sort()
        .join("|"),
    [productCurrencies]
  );

  useEffect(() => {
    // Helper to read current array from RHF and compute keys
    const currentArr = getValues(path) as {currency: string; price: number}[] | undefined;
    const currentKey = toKey(currentArr);

    // FREE MODE: seed once if empty, do nothing otherwise
    if (productSelectedPriceType !== "paid") {
      if (!currentArr || currentArr.length === 0) {
        const next = [{currency: "NGN", price: 0}];
        const nextKey = toKey(next);
        if (currentKey !== nextKey && lastAppliedKeyRef.current !== nextKey) {
          lastAppliedKeyRef.current = nextKey;
          replace(next);
        }
      }
      return; // ✅ never overwrite in free mode
    }

    // PAID MODE: enforce subset of product currencies
    if (productCurrencies.length === 0) return;

    const allowed = new Set(productCurrencies.map((c) => c.callPriceCurrency));
    const filtered = (currentArr ?? []).filter((v) => allowed.has(v.currency));

    const next =
      filtered.length > 0
        ? filtered
        : [{currency: productCurrencies[0].callPriceCurrency, price: 100}];

    const nextKey = toKey(next);

    if (currentKey !== nextKey && lastAppliedKeyRef.current !== nextKey) {
      lastAppliedKeyRef.current = nextKey;
      replace(next);
    }
  }, [productKey, productSelectedPriceType, replace, getValues, path]);
}
